// covered-by: frontends/carrie/src/ask-v2/review/__tests__/AskV2ReviewWorkspace.test.tsx
import {
  type AskPunttComparisonRegion,
  type AskPunttVersionComparison,
} from "@mg/schemas/src/christo/versionComparison";
import { Button } from "@radix-ui/themes";
import { useTranslation } from "react-i18next";

import { describeRegion } from "./versionChanges";

/**
 * The strip over a version comparison: how many windows changed, which pins
 * both artworks carry, and where the picked change or finding sits. Stepping
 * is J and K, or a click on a change.
 */
export function AskV2ReviewCompareStatus({
  comparison,
  changeAt,
  steppedRegion,
  hasSelectedComment,
  selectedRegion,
  pinsShown,
  onShowPins,
}: {
  comparison: AskPunttVersionComparison | null;
  changeAt: number | null;
  steppedRegion: AskPunttComparisonRegion | null;
  hasSelectedComment: boolean;
  selectedRegion: AskPunttComparisonRegion | null;
  pinsShown: "changes" | "findings";
  onShowPins: (kind: "changes" | "findings") => void;
}) {
  const { t } = useTranslation("ui");
  return (
    <div
      className="flex min-h-8 items-center gap-2 border-b border-puntt-neutral-gray-5 bg-base-white px-4 py-1 text-[12px] text-puntt-neutral-gray-11"
      data-testid="ask-v2-review-compare-status"
      role="status"
      aria-label={t("askV2.review.compareStatusLabel")}
    >
      {comparison ? (
        <>
          <span
            className="shrink-0 font-medium text-puntt-neutral-gray-12"
            data-testid="ask-v2-review-compare-change-count"
          >
            {t("askV2.review.compareChangeCount", {
              count: comparison.regions.length,
            })}
          </span>
          <div
            className="flex shrink-0 items-center gap-1"
            role="group"
            aria-label={t("askV2.review.comparePinsLabel")}
            data-testid="ask-v2-review-compare-pins"
          >
            {(["changes", "findings"] as const).map((kind) => (
              <Button
                key={kind}
                variant={pinsShown === kind ? "solid" : "soft"}
                color="gray"
                size="1"
                aria-pressed={pinsShown === kind}
                data-testid={`ask-v2-review-compare-pins-${kind}`}
                onClick={() => onShowPins(kind)}
              >
                {kind === "changes"
                  ? t("askV2.review.comparePinsChanges")
                  : t("askV2.review.comparePinsFindings")}
              </Button>
            ))}
          </div>
          {steppedRegion && changeAt != null ? (
            <span
              className="min-w-0 truncate"
              data-testid="ask-v2-review-compare-change-current"
            >
              <span className="font-medium text-puntt-neutral-gray-12">
                {t("askV2.review.compareChangePosition", {
                  current: changeAt + 1,
                  total: comparison.regions.length,
                })}
              </span>
              {describeRegion(steppedRegion)
                ? `: ${describeRegion(steppedRegion)}`
                : null}
            </span>
          ) : hasSelectedComment ? (
            <span
              className="min-w-0 truncate"
              data-testid="ask-v2-review-compare-finding-status"
            >
              {selectedRegion
                ? t("askV2.review.compareFindingChanged", {
                    number: selectedRegion.number,
                  })
                : t("askV2.review.compareFindingUnchanged")}
            </span>
          ) : null}
        </>
      ) : (
        <span data-testid="ask-v2-review-compare-not-compared">
          {t("askV2.review.compareNotCompared")}
        </span>
      )}
    </div>
  );
}
